/** @format */
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    AbstractPersonRule,
    AttainedCreditRangeRule,
    Code,
    CourseUnitRealisation,
    Education,
    EnrolmentForCourseUnitRealisationRule,
    PersonGroupDetails,
    PersonGroupMembershipRule,
    RuleResult,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import { combineLatest, from, map, Observable, of, take } from 'rxjs';
import { CourseUnitRealisationNameService, DowngradedService, LocaleService, ServiceDowngradeMappings, StaticMembers } from 'sis-common';
import { PersonRuleTypes, PersonRuleTypeTranslationKeys } from '../constant/personRuleTypes';
import { CommonCodeService } from '../service/common-code.service';
import { CourseUnitRealisationEntityService } from '../service/course-unit-realisation-entity.service';
import { EducationEntityService } from '../service/education-entity.service';
import { PersonGroupEntityService } from '../service/person-group-entity.service';

export interface PersonRuleTexts {
    courseUnitRealisationId: string;
    failedRequirementPersonRulesText: string[];
    orderingPersonRulesText: string[];
}

@StaticMembers<DowngradedService>()
@Injectable({
    providedIn: 'root',
})
export class PersonRuleService {
    static downgrade: ServiceDowngradeMappings = {
        moduleName: 'sis-components.person-rule-service',
        serviceName: 'personRuleService',
    };

    constructor(
        private translate: TranslateService,
        private localeService: LocaleService,
        private personGroupEntityService: PersonGroupEntityService,
        private courseUnitRealisationEntityService: CourseUnitRealisationEntityService,
        private educationEntityService: EducationEntityService,
        private nameService: CourseUnitRealisationNameService,
        private commonCodeService: CommonCodeService,
    ) {}

    getCalculationResultTexts(
        curId: string,
        requirementRuleResults: RuleResult[],
        orderingRuleResults: RuleResult[],
    ): Observable<PersonRuleTexts> {
        const requirementObservables = requirementRuleResults.map(result => this.fetchRuleInfoText(result.rule).pipe(take(1)));
        const orderingObservables = orderingRuleResults.map(result => this.fetchRuleInfoText(result.rule, true).pipe(take(1)));

        return combineLatest([...requirementObservables, ...orderingObservables]).pipe(
            map(results => {
                const reqRuleTexts = results.slice(0, requirementObservables.length);
                const ordRuleTexts = results.slice(requirementObservables.length);
                return {
                    courseUnitRealisationId: curId,
                    failedRequirementPersonRulesText: reqRuleTexts,
                    orderingPersonRulesText: ordRuleTexts,
                } as PersonRuleTexts;
            }),
        );
    }

    fetchRuleInfoText(rule: AbstractPersonRule, isOrderingRule?: boolean): Observable<string> {
        if (!Object.keys(PersonRuleTypeTranslationKeys).includes(rule.type)) return of('');

        const ruleType = rule.type as keyof typeof PersonRuleTypeTranslationKeys;
        const translationKey = PersonRuleTypeTranslationKeys[ruleType];

        switch (ruleType) {
            case PersonRuleTypes.PERSON_GROUP_MEMBERSHIP:
                return this.getPersonGroupInfoText(rule as PersonGroupMembershipRule);
            case PersonRuleTypes.ENROLMENT_FOR_COURSE_UNIT_REALISATION:
                return this.getEnrolmentForCurRuleInfoText(rule as EnrolmentForCourseUnitRealisationRule);
            case PersonRuleTypes.ATTAINED_CREDIT_RANGE:
                return this.getAttainedCreditRangeInfoText(rule as AttainedCreditRangeRule, !!isOrderingRule);
            default:
                return of(this.translate.instant(translationKey));
        }
    }

    getPersonGroupInfoText(personGroupRule: PersonGroupMembershipRule): Observable<string> {
        return this.personGroupEntityService.getDetailsByIds(personGroupRule?.personGroupIds).pipe(
            map((details: PersonGroupDetails[]) => details?.map(detail => this.localeService.localize(detail.name))),
            map(groupNames => `${this.translate.instant(PersonRuleTypeTranslationKeys.PersonGroupMembership)}: ${groupNames?.join(', ')}`),
        );
    }

    getEnrolmentForCurRuleInfoText(enrolmentForCurRule: EnrolmentForCourseUnitRealisationRule): Observable<string> {
        return this.courseUnitRealisationEntityService.getByIds(enrolmentForCurRule?.courseUnitRealisationIds).pipe(
            map(curs =>
                curs.map((cur: CourseUnitRealisation) => {
                    const localizedFullName = this.localeService.localize(this.nameService.generateFullName(cur));
                    if (cur.flowState === 'PUBLISHED') {
                        return localizedFullName;
                    }
                    const key = `FLOW_STATE.${cur.flowState}`;
                    return `${localizedFullName} (${this.translate.instant(key)})`;
                }),
            ),
            map(
                curNames =>
                    `${this.translate.instant(PersonRuleTypeTranslationKeys.EnrolmentForCourseUnitRealisation)}:\n${curNames.join('\n')}`,
            ),
        );
    }

    getAttainedCreditRangeInfoText(attainedCreditsRule: AttainedCreditRangeRule, isOrderingRule: boolean): Observable<string> {
        const educations$: Observable<Education[]> = this.educationEntityService.getByIds(attainedCreditsRule.educationIds);
        const code$: Observable<Code> = from(this.commonCodeService.getCode(attainedCreditsRule.degreeProgramTypeUrn));

        return combineLatest([educations$, code$]).pipe(
            map(([educationResults, codeResult]) => {
                let ruleName = this.translate.instant(PersonRuleTypeTranslationKeys[attainedCreditsRule.type]);
                ruleName += ':';
                if (isOrderingRule) {
                    ruleName += '\n- ';
                    ruleName += this.translate.instant('PERSON_RULE.ATTAINED_CREDIT_RANGE.STUDENT_ORDERING.TITLE');
                    ruleName += ': ';
                    ruleName += this.translate.instant(
                        `PERSON_RULE.ATTAINED_CREDIT_RANGE.STUDENT_ORDERING.${attainedCreditsRule.creditOrder}`,
                    );
                }
                if (attainedCreditsRule.creditRange) {
                    if (!_.has(attainedCreditsRule, 'creditRange.max') || _.isNil(attainedCreditsRule.creditRange.max)) {
                        ruleName += '\n- ';
                        ruleName += this.translate.instant('PERSON_RULE.ATTAINED_CREDIT_RANGE.CREDITS_WITHOUT_MAX', {
                            min: attainedCreditsRule.creditRange.min,
                        });
                    } else if (attainedCreditsRule.creditRange.min === 0) {
                        ruleName += '\n- ';
                        ruleName += this.translate.instant('PERSON_RULE.ATTAINED_CREDIT_RANGE.CREDITS_WITHOUT_MIN', {
                            max: attainedCreditsRule.creditRange.max,
                        });
                    } else if (attainedCreditsRule.creditRange.min && attainedCreditsRule.creditRange.max) {
                        ruleName += '\n- ';
                        ruleName += this.translate.instant('PERSON_RULE.ATTAINED_CREDIT_RANGE.CREDITS_WITH_MAX', {
                            max: attainedCreditsRule.creditRange.max,
                            min: attainedCreditsRule.creditRange.min,
                        });
                    }
                }

                if (!_.isEmpty(attainedCreditsRule.educationIds)) {
                    ruleName += '\n- ';
                    const educations: string[] = [];
                    const educationsById = _.keyBy(educationResults, 'id');

                    _.forEach(attainedCreditsRule.educationIds, id => {
                        const education = educationsById[id];
                        if (!_.isNil(education)) {
                            educations.push(this.localeService.localize(education.name));
                        }
                    });

                    ruleName += this.translate.instant('PERSON_RULE.ATTAINED_CREDIT_RANGE.EDUCATIONS', {
                        educations: educations.join(', '),
                    });
                }

                if (attainedCreditsRule.degreeProgramTypeUrn) {
                    ruleName += '\n- ';
                    const degreeProgramTypeName = codeResult?.name;
                    ruleName += this.translate.instant('PERSON_RULE.ATTAINED_CREDIT_RANGE.DEGREE_PROGRAM_TYPE', {
                        degreeProgramTypeName: this.localeService.localize(degreeProgramTypeName),
                    });
                }

                return ruleName;
            }),
        );
    }

    getAllRuleTypes() {
        return _.values(PersonRuleTypes);
    }

    getRuleName(type: string): string {
        const ruleType = type as keyof typeof PersonRuleTypeTranslationKeys;
        const translationKey = PersonRuleTypeTranslationKeys[ruleType];
        return this.translate.instant(translationKey);
    }

    isGroupMembershipRule(type: string) {
        return type === PersonRuleTypes.PERSON_GROUP_MEMBERSHIP;
    }

    isAttainedCreditRangeRule(type: string) {
        return type === PersonRuleTypes.ATTAINED_CREDIT_RANGE;
    }

    isEnrolmentForCurRule(type: string) {
        return type === PersonRuleTypes.ENROLMENT_FOR_COURSE_UNIT_REALISATION;
    }
}
